"use client";

import { useQuery } from "@tanstack/react-query";
import { useMachine } from "@xstate/react";
import {
  CheckCircle,
  CreditCard,
  Loader2,
  Radio,
  RefreshCw,
  XCircle,
} from "lucide-react";
import { useMemo, useState } from "react";
import type Stripe from "stripe";
import { PaymentTestSection } from "./PaymentTestSection";
import { paymentTestMachine } from "./stripe-payment-test.machine";

interface StripeTabProps {
  stripeReaderId?: string | null;
  onStripeReaderIdChange?: (readerId: string | null) => void;
  orgId?: number;
  practiceId?: number;
}

async function getReadersByPracticeIds(
  practiceIds: number[],
): Promise<Stripe.Terminal.Reader[]> {
  const qs = new URLSearchParams({
    practiceIds: `[${practiceIds.join(",")}]`,
  }).toString();

  const response = await fetch(`/api/stripe/get-readers-by-practiceIds?${qs}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch readers: ${response.statusText}`);
  }

  const responseData = await response.json();
  return responseData.readers || [];
}

export function StripeTab({
  stripeReaderId,
  onStripeReaderIdChange,
  orgId,
  practiceId,
}: StripeTabProps) {
  // Fetch readers for the practices
  const {
    data: stripeReaders,
    isLoading: isLoadingReaders,
    refetch,
  } = useQuery({
    queryKey: ["getReadersByPracticeIds", practiceId ? [practiceId] : []],
    queryFn: () => getReadersByPracticeIds(practiceId ? [practiceId] : []),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: practiceId !== undefined && practiceId !== null,
  });

  const availableReaders =
    stripeReaders?.filter((reader) => !reader.deleted) || [];
  const isLoading = isLoadingReaders;

  // Payment testing state machine - update context via input
  const initialContext = useMemo(
    () => ({
      readerId: stripeReaderId,
      orgId: orgId || null,
      amount: 10,
      paymentIntentId: null,
      clientSecret: null,
      readerStatus: null,
      error: null,
    }),
    [stripeReaderId, orgId],
  );

  const [paymentState, sendPayment] = useMachine(paymentTestMachine, {
    input: initialContext,
  });

  const [testAmount, setTestAmount] = useState("10.00");

  const handleSelectReader = (readerId: string) => {
    if (onStripeReaderIdChange) {
      onStripeReaderIdChange(readerId);
    }
  };

  const handleUnselectReader = () => {
    if (onStripeReaderIdChange) {
      onStripeReaderIdChange(null);
    }
  };

  const handleStartPaymentTest = () => {
    const amountCents = Math.round(Number.parseFloat(testAmount) * 100);
    sendPayment({ type: "START_TEST", amount: amountCents });
  };

  const handleRetry = () => {
    sendPayment({ type: "RETRY" });
  };

  const handleReset = () => {
    sendPayment({ type: "RESET" });
  };

  const handleCancel = () => {
    sendPayment({ type: "CANCEL" });
  };

  const getStatusColor = (status: string | undefined | null) => {
    switch (status) {
      case "online":
        return "text-green-600 bg-green-100";
      case "offline":
        return "text-red-600 bg-red-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getStatusIcon = (status: string | undefined | null) => {
    switch (status) {
      case "online":
        return <CheckCircle className="h-4 w-4" />;
      case "offline":
        return <XCircle className="h-4 w-4" />;
      default:
        return <Radio className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6 p-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">
          Stripe Terminal Integration
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Configure and test Stripe POS terminal for kiosk payments
        </p>
      </div>

      {/* Current Selection */}
      <div className="space-y-3 pb-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-700">
              Current Reader
            </h4>
            <p className="text-xs text-gray-500 mt-1">
              Selected reader for this kiosk session
            </p>
          </div>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isLoading}
            className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 disabled:opacity-50"
          >
            <RefreshCw
              className={`h-3 w-3 ${isLoading ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
          <div className="text-sm font-medium text-blue-900">
            {stripeReaderId || "Not configured"}
          </div>
          {stripeReaderId && (
            <button
              type="button"
              onClick={handleUnselectReader}
              className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
            >
              <XCircle className="h-3 w-3" />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Available Readers */}
      <div className="space-y-3">
        <div>
          <h4 className="text-sm font-medium text-gray-700">
            Available Readers
          </h4>
          <p className="text-xs text-gray-500 mt-1">
            Readers configured for Org ID: {orgId || "Not set"}
          </p>
        </div>

        {isLoading ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
            <Loader2 className="h-8 w-8 text-gray-400 mx-auto mb-2 animate-spin" />
            <p className="text-sm text-gray-600">Loading readers...</p>
          </div>
        ) : !orgId ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
            <XCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600">No Org ID configured</p>
            <p className="text-xs text-gray-500 mt-1">
              Set an Org ID in Settings to load readers
            </p>
          </div>
        ) : availableReaders.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
            <XCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600">No readers found</p>
            <p className="text-xs text-gray-500 mt-1">
              No Stripe Terminal readers configured for this organization
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {availableReaders.map((reader) => {
              const isSelected = reader.id === stripeReaderId;
              return (
                <button
                  key={reader.id}
                  type="button"
                  onClick={() => handleSelectReader(reader.id)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    isSelected
                      ? "bg-blue-50 border-blue-300 ring-2 ring-blue-500"
                      : "bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <div
                        className={`h-10 w-10 rounded-full flex items-center justify-center ${
                          isSelected ? "bg-blue-200" : "bg-gray-200"
                        }`}
                      >
                        <CreditCard
                          className={`h-5 w-5 ${
                            isSelected ? "text-blue-700" : "text-gray-600"
                          }`}
                        />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {reader.label || reader.id}
                        </p>
                        {isSelected && (
                          <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded">
                            Selected
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate font-mono">
                        {reader.id}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded flex items-center gap-1 ${getStatusColor(
                            reader.status,
                          )}`}
                          title="Stripe warns: status field is unreliable for payment flows"
                        >
                          {getStatusIcon(reader.status)}
                          {reader.status || "unknown"}
                        </span>
                        {reader.last_seen_at && (
                          <span
                            className="text-xs text-gray-400"
                            title="last_seen_at from Stripe API"
                          >
                            seen{" "}
                            {Math.round(
                              (Date.now() - reader.last_seen_at) / 1000,
                            )}
                            s ago
                          </span>
                        )}
                        {reader.device_type && (
                          <span className="text-xs text-gray-500">
                            {reader.device_type}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Test Payment Section */}
      <PaymentTestSection
        stripeReaderId={stripeReaderId}
        orgId={orgId}
        paymentState={paymentState}
        testAmount={testAmount}
        onAmountChange={setTestAmount}
        onStartTest={handleStartPaymentTest}
        onRetry={handleRetry}
        onReset={handleReset}
        onCancel={handleCancel}
      />
    </div>
  );
}
